/**
 * Centralized configuration service for managing environment variables
 * and application settings with type safety and validation
 */

export interface AppConfig {
  supabase: {
    url: string;
    anonKey: string;
  };
  features: {
    signupEnabled: boolean;
  };
  app: {
    environment: 'development' | 'production' | 'test';
    isDevelopment: boolean;
    isProduction: boolean;
    apiUrl: string;
  };
}

class ConfigService {
  private static instance: ConfigService;
  private config: AppConfig;

  private constructor() {
    this.config = this.loadConfig();
    this.validateConfig();
  }

  public static getInstance(): ConfigService {
    if (!ConfigService.instance) {
      ConfigService.instance = new ConfigService();
    }
    return ConfigService.instance;
  }

  private loadConfig(): AppConfig {
    const nodeEnv = process.env.NODE_ENV || 'development';
    const isDevelopment = nodeEnv === 'development';

    return {
      supabase: {
        url: this.getRequiredEnv('REACT_APP_SUPABASE_URL'),
        anonKey: this.getRequiredEnv('REACT_APP_SUPABASE_ANON_KEY'),
      },
      features: {
        signupEnabled: this.getBooleanEnv('REACT_APP_SIGNUP_ENABLED', true),
      },
      app: {
        environment: nodeEnv as 'development' | 'production' | 'test',
        isDevelopment,
        isProduction: nodeEnv === 'production',
        apiUrl: isDevelopment ? 'http://localhost:7100' : window.location.origin,
      },
    };
  }

  private getRequiredEnv(key: string): string {
    const value = process.env[key];
    if (!value) {
      throw new Error(`Required environment variable ${key} is not defined`);
    }
    return value;
  }

  private getBooleanEnv(key: string, defaultValue: boolean = false): boolean {
    const value = process.env[key];
    if (value === undefined) {
      return defaultValue;
    }
    return value.toLowerCase() === 'true';
  }

  private validateConfig(): void {
    const { supabase } = this.config;

    // Validate Supabase URL
    try {
      new URL(supabase.url);
    } catch {
      throw new Error(`Invalid SUPABASE_URL: ${supabase.url}`);
    }

    // Validate Supabase key format (basic check)
    if (!supabase.anonKey || supabase.anonKey.length < 10) {
      throw new Error('Invalid SUPABASE_ANON_KEY format');
    }
  }

  public get(): AppConfig {
    return { ...this.config };
  }

  public getSupabaseConfig() {
    return { ...this.config.supabase };
  }

  public getFeatureFlags() {
    return { ...this.config.features };
  }

  public getAppConfig() {
    return { ...this.config.app };
  }

  public isFeatureEnabled(feature: keyof AppConfig['features']): boolean {
    return this.config.features[feature];
  }

  public isDevelopment(): boolean {
    return this.config.app.isDevelopment;
  }

  public isProduction(): boolean {
    return this.config.app.isProduction;
  }

  public getApiUrl(): string {
    return this.config.app.apiUrl;
  }
}

// Export singleton instance
export const config = ConfigService.getInstance();

// Export convenience functions for common usage patterns
export const getSupabaseConfig = () => config.getSupabaseConfig();
export const isSignupEnabled = () => config.isFeatureEnabled('signupEnabled');
export const isDevelopment = () => config.isDevelopment();
export const isProduction = () => config.isProduction();
export const getApiUrl = () => config.getApiUrl();
